import { useState, useEffect } from 'react';
import { Select } from '@/components/common';
import { createSupportTicket } from '@/service/api/supportTicket';
import { TicketAttachmentUploader, UploadedAttachment } from './TicketAttachmentUploader';

// 工单类型选项配置
const TICKET_TYPE_OPTIONS = [
  { label: '账号相关问题', value: 'ACCOUNT' },
  { label: '视频制作 / AI 功能', value: 'VIDEO' },
  { label: '支付与充值交易', value: 'PAYMENT' },
  { label: '内容发布与审核', value: 'CONTENT' },
  { label: '其他疑问或功能建议', value: 'OTHER' },
];

// 期望联系时间选项配置
const PREFERRED_CALL_OPTIONS = [
  { label: '随时可联系', value: 'ANY_TIME' },
  { label: '工作日 09:00 - 18:00', value: 'WORKDAY_09_18' },
  { label: '工作日 09:00 - 22:00', value: 'WORKDAY_09_22' },
  { label: '每天 09:00 - 22:00', value: 'DAILY_09_22' },
  { label: '无需电话联系（仅通过工单反馈回复）', value: 'NO_CALL' },
];

export interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateTicketModal({ isOpen, onClose, onSuccess }: CreateTicketModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [formData, setFormData] = useState<Api.SupportTicket.CreateRequest>({
    ticket_type: 'ACCOUNT',
    preferred_call_at: 'ANY_TIME',
    description: '',
    contact_email: '',
    contact_phone: '',
    idempotency_key: '',
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ticket_type: 'ACCOUNT',
        preferred_call_at: 'ANY_TIME',
        description: '',
        contact_email: '',
        contact_phone: '',
        idempotency_key: `ticket-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      });
      setAttachments([]);
      setSubmitError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setSubmitError('请填写工单具体问题描述');
      return;
    }

    const phone = (formData.contact_phone || '').trim();
    if (phone && !/^\d{11}$/.test(phone)) {
      setSubmitError('联系手机号格式不正确，请输入 11 位手机号码');
      return;
    }

    const email = (formData.contact_email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmitError('联系邮箱格式不正确，请输入有效的邮箱地址（如 example@domain.com）');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const attachmentIds = attachments.map((item) => item.id);
      await createSupportTicket({
        ...formData,
        contact_phone: phone || undefined,
        contact_email: email || undefined,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('提交工单失败', err);
      setSubmitError(err?.message || '提交失败，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 relative animate-[fadeIn_0.15s_ease-out]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
          <i className="ri-add-circle-line text-emerald-600"></i>
          提交服务工单
        </h3>
        <p className="text-xs text-slate-500 mb-6">我们将在收到工单后第一时间跟进并予以解答。</p>

        {submitError && (
          <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-100 text-xs text-rose-600 flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmitTicket} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              工单类型 <span className="text-rose-500">*</span>
            </label>
            <Select
              options={TICKET_TYPE_OPTIONS}
              value={formData.ticket_type}
              onChange={(val) => setFormData({ ...formData, ticket_type: String(val) })}
              size="md"
              placeholder="请选择工单类型"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              期望电话联系时间 <span className="text-rose-500">*</span>
            </label>
            <Select
              options={PREFERRED_CALL_OPTIONS}
              value={formData.preferred_call_at}
              onChange={(val) => setFormData({ ...formData, preferred_call_at: String(val) })}
              size="md"
              placeholder="请选择期望电话联系时间"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">联系手机号</label>
              <input
                type="tel"
                maxLength={11}
                placeholder="请输入 11 位手机号码"
                value={formData.contact_phone || ''}
                onChange={(e) => {
                  setFormData({ ...formData, contact_phone: e.target.value });
                  if (submitError) setSubmitError(null);
                }}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">联系邮箱</label>
              <input
                type="email"
                placeholder="例如 user@example.com"
                value={formData.contact_email || ''}
                onChange={(e) => {
                  setFormData({ ...formData, contact_email: e.target.value });
                  if (submitError) setSubmitError(null);
                }}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              问题具体描述 <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={4}
              placeholder="请详细描述您遇到的问题、出现的现象或需求..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
              required
            />
          </div>

          {/* 附件上传区域 */}
          <TicketAttachmentUploader
            value={attachments}
            onChange={setAttachments}
            maxCount={5}
            disabled={submitting}
          />

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-medium transition-colors cursor-pointer"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium transition-colors shadow-sm cursor-pointer flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 提交中...
                </>
              ) : (
                '确认提交'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

